import moment from 'moment'

/** Formato de fechas del proyecto en API (project.current_start_date / current_end_date). */
const DATE_FORMAT = 'YYYY-MM-DD'

/** Formato mostrado en header y tabla semanal. */
const DISPLAY_FORMAT = 'DD/MM/YYYY'

/**
 * Utilidades de semanas para la planificación semanal (weekly.vue).
 *
 * Fuente de datos: project.current_start_date y project.current_end_date.
 *
 * Reglas de negocio (calendario ISO: lunes = inicio, domingo = cierre):
 *
 * - Semana 1 (parcial): desde current_start_date hasta el domingo de esa misma semana.
 *   Ejemplo: inicio jueves 05/12/2024 → S01 = 05/12 – 08/12 (4 días: jue–dom).
 *   Si el proyecto empieza en lunes, la semana 1 ya es completa (7 días).
 *
 * - Semanas 2 en adelante: bloques lunes–domingo completos hasta current_end_date.
 *   Ejemplo: S02 = 09/12 – 15/12, S03 = 16/12 – 22/12, etc.
 *
 * - La última semana puede ser parcial si current_end_date cae antes del domingo.
 *
 * Consumido por weekly.vue para:
 * - Navegación ← → del header (currentWeekIndex)
 * - Etiquetas weekLabel y weekRange del header; start/end para la tabla
 */

/**
 * Construye la lista ordenada de semanas del proyecto.
 *
 * @param {string} startDate - current_start_date (YYYY-MM-DD)
 * @param {string} endDate - current_end_date (YYYY-MM-DD)
 * @returns {Array<{ number, weekNumber, year, start, end, label, range }>}
 */
export function buildProjectWeeks(startDate, endDate) {
  const projectStart = parseProjectDate(startDate)
  const projectEnd = parseProjectDate(endDate)

  if (!projectStart || !projectEnd || projectEnd.isBefore(projectStart, 'day')) {
    return []
  }

  const weeks = []
  /** Puntero al primer día de la semana que estamos generando. */
  let cursor = projectStart.clone()
  let weekNumber = 1

  while (cursor.isSameOrBefore(projectEnd, 'day')) {
    let weekStart = cursor.clone()
    let weekEnd

    if (weekNumber === 1) {
      // Semana 1: parcial hasta el domingo de la semana ISO que contiene el inicio.
      if (weekStart.isoWeekday() === 1) {
        // Lunes → semana completa lunes–domingo.
        weekEnd = weekStart.clone().add(6, 'days')
      } else {
        // Cualquier otro día → hasta el domingo de esa semana (isoWeekday 7).
        weekEnd = weekStart.clone().isoWeekday(7)
      }
    } else {
      // Semanas completas: siempre lunes (1) a domingo (+6 días).
      if (weekStart.isoWeekday() !== 1) {
        weekStart = weekStart.isoWeekday(8) // 8 = lunes de la semana ISO siguiente
      }
      weekEnd = weekStart.clone().add(6, 'days')
    }

    // Recortar la última semana si el proyecto termina antes del domingo.
    if (weekEnd.isAfter(projectEnd, 'day')) {
      weekEnd = projectEnd.clone()
    }

    weeks.push(formatWeek(weekNumber, weekStart, weekEnd))

    // Siguiente semana empieza el día después del fin de la actual.
    cursor = weekEnd.clone().add(1, 'day')
    weekNumber += 1
  }

  return weeks
}

/**
 * Devuelve el índice (0-based) de la semana que contiene una fecha concreta.
 *
 * Útil para posicionar la navegación en una semana específica (p. ej. hoy,
 * un datepicker o un enlace profundo).
 *
 *
 * Fallback: si no hay semanas, la fecha es inválida o cae fuera del rango del
 * proyecto, devuelve 0 (primera semana). Ver findCurrentWeekIndex().
 */
export function findWeekIndexForDate(weeks, date) {
  if (!weeks.length) {
    return 0
  }

  const target = parseProjectDate(date)
  if (!target) {
    return 0
  }

  const index = weeks.findIndex((week) => (
    target.isSameOrAfter(week.start, 'day') && target.isSameOrBefore(week.end, 'day')
  ))

  return index >= 0 ? index : 0
}

/**
 * Atajo: índice de la semana que contiene la fecha de hoy.
 * Usado en weekly.vue → syncCurrentWeekIndex() al cargar o cambiar fechas del proyecto.
 */
export function findCurrentWeekIndex(weeks) {
  return findWeekIndexForDate(weeks, moment())
}

/** Normaliza fechas del API o moment a inicio de día; null si no es válida. */
function parseProjectDate(value) {
  if (!value) {
    return null
  }

  const parsed = moment(value, [DATE_FORMAT, moment.ISO_8601], true)
  return parsed.isValid() ? parsed.startOf('day') : null
}

/**
 * Formatea una semana para la UI del header y como fuente de fechas para componentes hijos.
 *
 * - label / range: weekly-planning-header
 * - start / end: moment en YYYY-MM-DD vía weekly.vue → activities-weekly-table
 */
function formatWeek(number, start, end) {
  const weekNumber = String(number).padStart(2, '0')
  const year = start.year()
  const rangeStart = start.format(DISPLAY_FORMAT)
  const rangeEnd = end.format(DISPLAY_FORMAT)
  const range = rangeStart === rangeEnd
    ? rangeStart
    : `${rangeStart} – ${rangeEnd}`

  return {
    number,
    weekNumber,
    year,
    start: start.clone(),
    end: end.clone(),
    label: `Semana ${weekNumber} · ${year}`,
    range,
  }
}
